<script setup>
import { ref, computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { CARD_STYLES } from '../../config/cardStyles.js'
import BaseCheckbox from '../ui/BaseCheckbox.vue'
import TermsModal from '../ui/TermsModal.vue'

const { t: $t } = useI18n()

const props = defineProps({
  selectedCurrency: {
    type: Object,
    required: true
  },
  wallet: {
    type: Object,
    default: null
  },
  initialBgStyle: {
    type: Number,
    default: 0
  },
  initialMessage: {
    type: String,
    default: 'Enjoy crypto with Changelly!'
  }
})

const emit = defineEmits(['style-selected', 'next-step', 'live-change'])

// Card state
const bgStyle = ref(props.initialBgStyle)
const message = ref(props.initialMessage)
const isTransitioning = ref(false)

// Terms of Service state
const tosAccepted = ref(false)
const showTermsModal = ref(false)
const TOS_STORAGE_KEY = 'giftbox_tos_accepted'

// Load ToS acceptance from localStorage on mount
onMounted(() => {
  const savedTosAcceptance = localStorage.getItem(TOS_STORAGE_KEY)
  if (savedTosAcceptance === 'true') {
    tosAccepted.value = true
  }
})

// Watch for ToS acceptance changes and save to localStorage
const handleTosChange = (accepted) => {
  tosAccepted.value = accepted
  if (accepted) {
    localStorage.setItem(TOS_STORAGE_KEY, 'true')
  } else {
    localStorage.removeItem(TOS_STORAGE_KEY)
  }
}

// Handle ToS modal open
const handleOpenTermsModal = () => {
  showTermsModal.value = true
}

// Handle ToS acceptance from modal
const handleAcceptTerms = () => {
  tosAccepted.value = true
  localStorage.setItem(TOS_STORAGE_KEY, 'true')
}

// Get card styles from centralized config
const cardStyles = CARD_STYLES

const handleStyleSelect = (styleId) => {
  bgStyle.value = styleId
  // Emit live change for instant card update
  emit('live-change', { bgStyle: styleId, message: message.value })
  emit('style-selected', { bgStyle: styleId, message: message.value })
}

const handleMessageChange = () => {
  // Emit live change when message is updated
  emit('live-change', { bgStyle: bgStyle.value, message: message.value })
}

const handleContinue = async () => {
  if (isTransitioning.value || !tosAccepted.value) return

  isTransitioning.value = true

  // Save the selection first
  emit('style-selected', { bgStyle: bgStyle.value, message: message.value })

  // Wait for animation to show the locked-in state
  await new Promise(resolve => setTimeout(resolve, 800))

  // Then trigger transition
  emit('next-step')
}
</script>

<template>
  <div class="flex flex-col gap-6 w-full">
    <!-- Style Selector -->
    <div class="flex flex-col gap-3">
      
      <div class="grid grid-cols-5 gap-3">
        <button
          v-for="style in cardStyles"
          :key="style.id"
          @click="handleStyleSelect(style.id)"
          class="relative rounded-lg cursor-pointer transition-all duration-200 hover:opacity-80 hover:-translate-y-0.5 overflow-hidden"
          :class="{ 'ring-2 ring-vault-purple-400 ring-offset-2': bgStyle === style.id }"
        >
          <div
            class="w-full aspect-square rounded-md"
            :style="{
              background: style.thumbnailWebP || style.thumbnail
                ? `url(${style.thumbnailWebP || style.thumbnail}), ${style.background}`
                : style.background,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat'
            }"
          >
            <!-- WebP support with fallback to JPEG and then gradient -->
            <picture v-if="style.thumbnailWebP || style.thumbnail" class="hidden">
              <source v-if="style.thumbnailWebP" :srcset="style.thumbnailWebP" type="image/webp" />
              <img
                v-if="style.thumbnail"
                :src="style.thumbnail"
                :alt="style.name"
                class="w-full h-full object-cover rounded-md"
                loading="lazy"
              />
            </picture>
          </div>
        </button>
      </div>
    </div>

    <!-- Message Input -->
    <div class="flex flex-col gap-3">

      <div class="relative">
        <input
          v-model="message"
          @input="handleMessageChange"
          type="text"
          placeholder="Enjoy crypto!"
          class="w-full px-4 py-3 pr-16 bg-vault-bg-secondary border-2 border-transparent rounded-lg focus:border-vault-purple-400 focus:outline-none transition-colors"
          maxlength="32"
        />
        <div class="absolute right-4 top-1/2 -translate-y-1/2 text-xs text-gray-400 pointer-events-none">
          {{ message.length }}/32
        </div>
      </div>
    </div>

    <!-- Terms of Service Checkbox -->
    <BaseCheckbox
      :model-value="tosAccepted"
      @update:model-value="handleTosChange"
      @link-click="handleOpenTermsModal"
    >
      {{ $t('giftStyle.tosPrefix', 'I accept the') }}
      <a href="#" @click.prevent>{{ $t('giftStyle.tosLink', 'Terms of Service') }}</a>
    </BaseCheckbox>

    <!-- Continue Button -->
    <div class="flex gap-3">
      <button
        @click="handleContinue"
        :disabled="isTransitioning || !tosAccepted"
        class="btn-primary w-full transition-all duration-200"
        :class="{ 'opacity-50 cursor-not-allowed': isTransitioning || !tosAccepted }"
      >
        {{ isTransitioning ? '✓ ' + $t('giftStyle.confirmed', 'Confirmed') : $t('giftStyle.continue', 'Continue') }}
      </button>
    </div>
  </div>

  <!-- Terms of Service Modal -->
  <TermsModal
    :open="showTermsModal"
    @close="showTermsModal = false"
    @accept="handleAcceptTerms"
  />
</template>

<style scoped>
/* No custom styles needed - all handled by Tailwind */
</style>
